import { getNodeIdsWithIncomingEdge } from '@/components/Recipe/utils/edgeSelectionGeometry';
import { HandleType } from '@/enums/handle-type.enum';
import { NodeType } from '@/enums/node-type.enum';
import { EMPTY_TOOL_CONFIG } from '@/types/tool-config.types';
import type { Edge, Node } from '@/types/node';
import type { ToolConfig, ToolConfigInput, ToolConfigWithInputs } from '@/types/tool-config.types';
import type {
  DraftInputOverride,
  DraftOutputOverride,
  WorkflowApp,
  WorkflowAppDraftOverrides,
} from '@/types/workflow-app.types';
import type { InputId, NodeId, NodeType as WasmNodeType } from 'web';

/** WASM {@link WasmNodeType} values exposed as app-builder inputs. */
const APP_BUILDER_INPUT_NODE_TYPES_LIST = [
  'promptV3',
  'import',
  'media_iterator',
  'string',
  'multilora',
  'color_palette',
  'muxv2',
  'seed',
  'array',
  'integer',
  'boolean',
] as const satisfies ReadonlyArray<WasmNodeType>;

const APP_BUILDER_INPUT_NODE_TYPES = new Set<string>(APP_BUILDER_INPUT_NODE_TYPES_LIST);

export interface AppBuilderGraph {
  nodes: Node[];
  edges: Edge[];
}

/** True when this tool input's outgoing value feeds at least one graph-required handle. */
export function isForcedMandatoryInputNode(
  nodeId: NodeId,
  edges: Edge[],
  isInputRequired: (nodeId: NodeId, inputId: InputId) => boolean,
): boolean {
  for (const edge of edges) {
    if (edge.source !== nodeId) {
      continue;
    }
    const inputId = edge.targetHandle?.split('-input-')[1];
    if (inputId && isInputRequired(edge.target, inputId)) {
      return true;
    }
  }
  return false;
}

function isAppBuilderInputNode(node: Node) {
  return !!node.type && APP_BUILDER_INPUT_NODE_TYPES.has(node.type);
}

/**
 * The text a static Prompt or String hands on, as its executor does: a Prompt's own text when it has
 * no variables, a String's `result.string`.
 */
function staticSourceText(node: Node): string | undefined {
  const data = node.data as { prompt?: unknown; inputNodes?: unknown[]; result?: { string?: unknown } };
  const text =
    node.type === NodeType.PromptV3 && !data.inputNodes?.length
      ? data.prompt
      : node.type === NodeType.String
        ? data.result?.string
        : undefined;
  return typeof text === 'string' ? text : undefined;
}

/**
 * Whether a source hands a List options known before a run: an Array, or a Prompt or String whose text
 * the List's delimiter splits with no blank segment (the editor keeps blanks, the run drops them, so
 * the indexes would disagree), feeding Lists alone so nothing else edits the text the pick is made from.
 */
function isStaticSource(
  source: Node,
  list: Node,
  targetsBySource: ReadonlyMap<NodeId, readonly NodeId[]>,
  nodesById: ReadonlyMap<NodeId, Node>,
): boolean {
  if (source.type === NodeType.Array) {
    return true;
  }
  const text = staticSourceText(source);
  const delimiter = (list.data as { delimiter?: string }).delimiter;
  if (text === undefined || !delimiter || text.split(delimiter).some((segment) => !segment.trim())) {
    return false;
  }
  return (targetsBySource.get(source.id) ?? []).every((targetId) => {
    const target = nodesById.get(targetId);
    return target?.type === NodeType.MuxV2 && !(target.data as { isIterator?: boolean }).isIterator;
  });
}

/**
 * A List selector fed only by static sources that nothing feeds (an Array, a Prompt, a String): its
 * options are known before a run, so the tool takes the pick from it, and the sources behind it are
 * not inputs. A source a lock keeps out of the tool keeps its List out of this rule, or the List would
 * hand on the values the lock hides.
 * KEEP_IN_SYNC(weavy/core/src/modules/recipe/utils/recipe-inspect.utils.ts#getFedSelectorOptions)
 */
function isFedSelector(
  node: Node | undefined,
  sourceIds: readonly NodeId[],
  nodesById: ReadonlyMap<NodeId, Node>,
  sourcesByTarget: ReadonlyMap<NodeId, readonly NodeId[]>,
  targetsBySource: ReadonlyMap<NodeId, readonly NodeId[]>,
  lockSnapshot: ReadonlySet<string> | null,
): boolean {
  if (node?.type !== NodeType.MuxV2 || (node.data as { isIterator?: boolean }).isIterator) {
    return false;
  }
  // A text source must be the List's only one: the run splits only the text it is handed.
  if (sourceIds.length > 1 && sourceIds.some((id) => nodesById.get(id)?.type !== NodeType.Array)) {
    return false;
  }
  return (
    sourceIds.length > 0 &&
    sourceIds.every((id) => {
      const source = nodesById.get(id);
      return (
        !!source &&
        (sourcesByTarget.get(id)?.length ?? 0) === 0 &&
        !isKeptOutByLock(source, lockSnapshot) &&
        isStaticSource(source, node, targetsBySource, nodesById)
      );
    })
  );
}

/**
 * The nodes feeding the given outputs, of any type: the leaves upstream of them, and any List selector
 * fed by static sources in their place. Also returns the ids of those selectors.
 */
export function getToolInputLeaves(
  nodes: Node[],
  edges: Edge[],
  rootIds: readonly NodeId[],
  /** The config's lock snapshot; see {@link isKeptOutByLock}. Null when none has been taken. */
  lockedAtConversion: readonly string[] | null = null,
): { leafIds: Set<NodeId>; fedSelectorIds: Set<NodeId> } {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const lockSnapshot = lockedAtConversion ? new Set(lockedAtConversion) : null;
  const sourcesByTarget = new Map<NodeId, NodeId[]>();
  const targetsBySource = new Map<NodeId, NodeId[]>();
  for (const edge of edges) {
    const sources = sourcesByTarget.get(edge.target) ?? [];
    sources.push(edge.source);
    sourcesByTarget.set(edge.target, sources);
    const targets = targetsBySource.get(edge.source) ?? [];
    targets.push(edge.target);
    targetsBySource.set(edge.source, targets);
  }
  const inputIds = new Set<NodeId>();
  const fedSelectorIds = new Set<NodeId>();
  const visited = new Set<NodeId>();
  const walk = (id: NodeId) => {
    if (visited.has(id)) return;
    visited.add(id);
    const sources = sourcesByTarget.get(id) ?? [];
    if (isFedSelector(nodesById.get(id), sources, nodesById, sourcesByTarget, targetsBySource, lockSnapshot)) {
      fedSelectorIds.add(id);
      inputIds.add(id);
    } else if (sources.length === 0) {
      inputIds.add(id);
    } else {
      sources.forEach(walk);
    }
  };
  rootIds.forEach(walk);
  return { leafIds: inputIds, fedSelectorIds };
}

/** {@link getToolInputLeaves}, keeping the types the Tool tab lists. */
function collectToolInputNodes(
  nodes: Node[],
  edges: Edge[],
  rootIds: readonly NodeId[],
  lockedAtConversion: readonly string[] | null = null,
): { inputNodes: Node[]; fedSelectorIds: Set<NodeId> } {
  const { leafIds, fedSelectorIds } = getToolInputLeaves(nodes, edges, rootIds, lockedAtConversion);
  // A declared output is that state on its own. The walk treats a leaf output as an input of
  // itself, and an output that also feeds another output as an input of that one. Neither is an input.
  const outputIds = new Set(rootIds);
  return {
    inputNodes: nodes.filter((node) => leafIds.has(node.id) && !outputIds.has(node.id) && isAppBuilderInputNode(node)),
    fedSelectorIds,
  };
}

/**
 * Whether a canvas lock keeps a node out of the tool: the same rule as core's inspect. Without a
 * snapshot (none taken yet) every locked node is out, as before the Tool tab. With one, only the nodes
 * locked when it was taken are: a lock decides exposure once, and a node locked after that is treated
 * as unlocked. Types the Tool tab cannot show keep the lock, since nothing would let the builder hide
 * them again.
 * KEEP_IN_SYNC(weavy/core/src/modules/recipe/utils/recipe-inspect.utils.ts#isKeptOutByLock)
 */
export function isKeptOutByLock(
  node: Pick<Node, 'id' | 'type' | 'data'>,
  lockedAtConversion: ReadonlySet<string> | null,
): boolean {
  if (!node.data.isLocked) {
    return false;
  }
  if (!lockedAtConversion) {
    return true;
  }
  return lockedAtConversion.has(node.id) || !node.type || !APP_BUILDER_INPUT_NODE_TYPES.has(node.type);
}

/**
 * App-builder input nodes: leaf nodes (no incoming edges) upstream of the given
 * workflow-app output node ids, after allowed-type and leaf-node rules.
 * Unknown output ids (not present in `nodes`) are ignored as roots.
 */
export function getWorkflowAppInputNodes(
  nodes: Node[],
  edges: Edge[],
  workflowAppOutputNodeIds: readonly NodeId[],
): Node[] {
  const nodeIds = new Set(nodes.map((n) => n.id));
  const roots = workflowAppOutputNodeIds.filter((id) => nodeIds.has(id));
  return collectToolInputNodes(nodes, edges, roots).inputNodes;
}

/**
 * Recomputes draft workflow-app input overrides from the current graph.
 *
 * Finds leaf nodes upstream of declared app outputs (same subgraph rule as Design App),
 * filtered by app-builder rules (allowed types, leaf nodes only),
 * keeps existing draft entries for those nodes so exposure/required/disabled
 * choices are preserved, appends core's defaults for any new qualifying nodes, drops
 * entries for nodes no longer inputs, and reassigns contiguous `order` values.
 */
export function deriveWorkflowAppInputs({
  currentInputs,
  outputs,
  nodes,
  edges,
  lockedAtConversion = null,
}: {
  currentInputs: DraftInputOverride[];
  outputs: DraftOutputOverride[];
  nodes: Node[];
  edges: Edge[];
  /** The config's lock snapshot; see {@link isKeptOutByLock}. Null when none has been taken. */
  lockedAtConversion?: readonly string[] | null;
}): DraftInputOverride[] {
  const nodeIds = new Set(nodes.map((n) => n.id));
  const { inputNodes, fedSelectorIds } = collectToolInputNodes(
    nodes,
    edges,
    outputs.map((output) => output.nodeId).filter((id) => nodeIds.has(id)),
    lockedAtConversion,
  );
  const inputNodeIds = new Set(inputNodes.map((node) => node.id));

  // Listed as hidden rather than left out, so the builder sees them and can expose one.
  const snapshot = lockedAtConversion ? new Set(lockedAtConversion) : null;
  const lockedIds = new Set(inputNodes.filter((node) => isKeptOutByLock(node, snapshot)).map((node) => node.id));
  const retainedInputs = [...currentInputs]
    .sort((a, b) => a.order - b.order)
    .filter((input) => inputNodeIds.has(input.nodeId))
    .map((input) => (lockedIds.has(input.nodeId) ? { ...input, exposed: false } : input));
  const retainedInputIds = new Set(retainedInputs.map((input) => input.nodeId));
  const newInputs = inputNodes
    .filter((node) => !retainedInputIds.has(node.id))
    .map((node, index): DraftInputOverride => ({
      nodeId: node.id,
      // A fed selector became an input after tools were already built, so it starts hidden; see core.
      exposed: isExposedByDefault(node) && !lockedIds.has(node.id) && !fedSelectorIds.has(node.id),
      order: retainedInputs.length + index,
    }));

  return [...retainedInputs, ...newInputs].map((input, order) => ({ ...input, order }));
}

/**
 * Graph-derived `base` rows (what the drawer lists) plus stored output list, per-input overrides,
 * and input order from `overlay` (optimistic write or last store snapshot). Choosing either
 * snapshot alone drops newly listed inputs, the field that just saved, or a reorder still in flight.
 */
export function overlayToolConfig(
  base: ToolConfigWithInputs,
  overlay: ToolConfig | null | undefined,
  graphNodeIds?: ReadonlySet<string>,
): ToolConfigWithInputs {
  if (!overlay) {
    return base;
  }
  // A stored config can lack either list. The base already carries the import for a missing `outputs`.
  const overlayInputs = Array.isArray(overlay.inputs) ? overlay.inputs : [];
  const overlayByNodeId = new Map(overlayInputs.map((input) => [input.nodeId, input]));
  const mergedByNodeId = new Map(
    base.inputs.map((input) => [input.nodeId, overlayByNodeId.get(input.nodeId) ?? input]),
  );
  // A stored input the graph does not list right now (an edge cut mid-edit, an output removed and
  // re-added) keeps its row, so its decision is still there when it comes back. Not once its node is
  // deleted, or the rows would pile up past the route's limit.
  for (const input of overlayInputs) {
    if (!mergedByNodeId.has(input.nodeId) && (!graphNodeIds || graphNodeIds.has(input.nodeId))) {
      mergedByNodeId.set(input.nodeId, input);
    }
  }
  const orderedNodeIds: string[] = [];
  const seen = new Set<string>();
  for (const { nodeId } of overlayInputs) {
    if (!mergedByNodeId.has(nodeId) || seen.has(nodeId)) {
      continue;
    }
    orderedNodeIds.push(nodeId);
    seen.add(nodeId);
  }
  for (const { nodeId } of base.inputs) {
    if (seen.has(nodeId)) {
      continue;
    }
    orderedNodeIds.push(nodeId);
    seen.add(nodeId);
  }
  return {
    version: overlay.version ?? base.version,
    outputs: Array.isArray(overlay.outputs) ? overlay.outputs : base.outputs,
    inputs: orderedNodeIds.map((nodeId) => mergedByNodeId.get(nodeId)!),
    // Nothing derives metadata from the graph, so the overlay is the only source.
    ...(overlay.metadata === undefined ? {} : { metadata: overlay.metadata }),
    // Core's, read back so the store keeps it; a write never sets it.
    ...(overlay.lockedAtConversion === undefined ? {} : { lockedAtConversion: overlay.lockedAtConversion }),
  };
}

/** Moves one input in the stored list. Order is array position on PATCH. */
export function reorderToolConfigInputs(
  config: ToolConfigWithInputs,
  fromIndex: number,
  toIndex: number,
): ToolConfigWithInputs {
  const { inputs } = config;
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= inputs.length || toIndex >= inputs.length) {
    return config;
  }
  const next = inputs.slice();
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return { ...config, inputs: next };
}

/** Moves one output in the stored list. Order is array position on PATCH. */
export function reorderToolConfigOutputs(
  config: ToolConfigWithInputs,
  fromIndex: number,
  toIndex: number,
): ToolConfigWithInputs {
  const { outputs } = config;
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= outputs.length ||
    toIndex >= outputs.length
  ) {
    return config;
  }
  const next = outputs.slice();
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return { ...config, outputs: next };
}

/** The output handle core reads a declared node from: the first in the node's saved `handles.output`. */
function firstOutputHandleId(node: Node): string | undefined {
  const output = node.data?.handles?.output;
  if (!output) {
    return undefined;
  }
  return Array.isArray(output) ? output[0] : Object.keys(output)[0];
}

/**
 * Node types core runs with `ModelNodeExecutor`. KEEP_IN_SYNC with
 * `core/src/modules/recipe-runner/services/node-executor/node-type-to-executor.ts`.
 */
const MODEL_EXECUTOR_NODE_TYPES: ReadonlySet<string> = new Set([
  'custommodelV2',
  'wildcard',
  'wildcardV2',
  'custommodel',
  'prompt_enhance',
  'sd_sketch',
  'sd_upscale',
  'sd_img2video',
  'sd_image23d',
  'nim_cc',
  'luma_video',
  'rw_video',
  'kling',
  'mochiv1',
  'minimax',
  'minimax_i2v',
  'flux_pro',
  'flux_fast',
  'flux_lora',
  'ig_text2image',
  'ig_describe',
  'meshy_image23d',
  'any_llm',
  'sd_inpaint',
  'sd_outpaint',
  'image2image',
  'text2image',
  'sd_text2image',
  'br_text2image',
  'controlNet',
  'br_vector',
  'bgremove',
  'sd_bgrmv',
  'objectremove',
  'rw_aleph2',
]);

/**
 * Whether declaring `producer` returns what a legacy Output wired from its `sourceHandleId` did.
 * Core reads a declared node off its first output handle, so a wire from that one always does. A
 * model that runs writes its result into every handle and both reads take that whole result
 * (`allOutput`), so a wire from any of its handles does too — a 3D model's `3D_model` comes after its
 * `image`. Not while it is locked: a locked node doesn't run, and core then reads each handle's saved
 * value.
 */
function declaredProducerReadsWire(producer: Node, sourceHandleId: string | undefined): boolean {
  if (sourceHandleId === undefined) {
    return false;
  }
  if (sourceHandleId === firstOutputHandleId(producer)) {
    return true;
  }
  return MODEL_EXECUTOR_NODE_TYPES.has(producer.type ?? '') && !producer.data?.isLocked;
}

/** Handle types a tool can output. A node is offered as an output only if it has one of these. */
export const TOOL_OUTPUT_HANDLE_TYPES: readonly HandleType[] = [
  HandleType.Image,
  HandleType.Video,
  HandleType.Audio,
  HandleType.ThreeDee,
  HandleType.Text,
];

/**
 * What the import needs from the live graph beyond the saved nodes: the old tab's input settings,
 * and whether a handle carries a type a tool can output, which only the WASM graph knows.
 */
export interface LegacyImportContext {
  inputSettings?: LegacyInputSettings | null;
  /** Absent means every handle counts, which only a caller without the WASM graph should rely on. */
  isOutputHandle?: (nodeId: NodeId, handleId: string) => boolean;
}

/** Node types that can never be a tool output, whatever they are wired to. */
export const NODE_TYPES_NOT_ELIGIBLE_FOR_TOOL_OUTPUT: ReadonlySet<string> = new Set([
  'custom_group',
  'stickynote',
  'target',
  'router',
]);

/** Steps a wire back past routers, which pass their single input through, to the wire that feeds them. */
function throughRouters(wire: Edge, nodesById: ReadonlyMap<NodeId, Node>, edges: Edge[]): Edge {
  const visited = new Set<NodeId>();
  let current = wire;
  while (nodesById.get(current.source)?.type === 'router' && !visited.has(current.source)) {
    visited.add(current.source);
    const feeds = edges.filter((edge) => edge.target === current.source && nodesById.has(edge.source));
    if (feeds.length !== 1) {
      break;
    }
    current = feeds[0];
  }
  return current;
}

/**
 * The outputs of a tool built before the Tool tab: every node wired into a `workflow_output` node,
 * in the order of those nodes. The Tool tab never offers that node, so without this a legacy tool
 * opens with no outputs at all.
 *
 * A router only passes its input through, so the node behind it is the one read, past any chain of
 * routers. Through a router a legacy tool returned only a model's first result; declared, it returns
 * all of them, as a direct wire already does.
 *
 * The `workflow_output` node itself stays the output wherever declaring the node behind it would
 * change the tool: core runs a declared node off its first output handle, so a wire from any other
 * handle, unless the node is a model that isn't locked (see {@link declaredProducerReadsWire}); a node the Tool tab could not offer as an output (a router fed by nothing, by more than one
 * wire, or by another router in a loop, one with nothing wired in, or one whose handle carries a type a tool can't output, such as
 * a mask or a number);
 * an output node fed by more than one wire; or a second output node fed by a node already declared.
 * Core runs a declared `workflow_output` exactly as it runs a legacy one.
 */
export function getLegacyToolOutputs(
  { nodes, edges }: AppBuilderGraph,
  isOutputHandle: LegacyImportContext['isOutputHandle'] = () => true,
): ToolConfig['outputs'] {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const incomingEdgeTargets = getNodeIdsWithIncomingEdge(edges);
  const outputIds = new Set<NodeId>();
  for (const outputNode of nodes) {
    if (outputNode.type !== NodeType.WorkflowOutput) {
      continue;
    }
    const wires = edges.filter((edge) => edge.target === outputNode.id && nodesById.has(edge.source));
    if (wires.length === 0) {
      continue;
    }
    const wire = throughRouters(wires[0], nodesById, edges);
    const producer = nodesById.get(wire.source)!;
    // Split the way core's runner reads an edge, so both agree on which handle it leaves from.
    const sourceHandleId = wire.sourceHandle?.split('-output-')[1];
    const canDeclareProducer =
      wires.length === 1 &&
      sourceHandleId !== undefined &&
      declaredProducerReadsWire(producer, sourceHandleId) &&
      isOutputHandle(producer.id, sourceHandleId) &&
      !NODE_TYPES_NOT_ELIGIBLE_FOR_TOOL_OUTPUT.has(producer.type ?? '') &&
      incomingEdgeTargets.has(producer.id) &&
      !outputIds.has(producer.id);
    outputIds.add(canDeclareProducer ? producer.id : outputNode.id);
  }
  return [...outputIds].map((nodeId) => ({ nodeId }));
}

/** One node's settings from the old Design App tab, kept in `recipes.designAppMetadata`. */
export interface LegacyInputSetting {
  order?: number;
  exposed?: boolean;
  required?: boolean;
  description?: string;
}

export type LegacyInputSettings = Readonly<Record<string, LegacyInputSetting | undefined>>;

/**
 * The order core's legacy read lists input types in (`getDesignAppInputs`), which is the order a
 * node with no stored setting takes. Types the Tool tab never lists fall away on their own.
 */
const LEGACY_INPUT_TYPE_RANK: Record<string, number> = {
  prompt: 0,
  promptV3: 0,
  string: 1,
  import: 2,
  media_iterator: 3,
  multilora: 4,
  color_palette: 5,
  muxv2: 6,
  seed: 7,
  array: 7,
  integer: 7,
  boolean: 7,
  mux: 7,
  number_selector: 7,
};
/** Rank of a LoRA in single view, which the legacy read lists last. */
const LEGACY_SINGLE_LORA_RANK = 8;
/** Inputs the legacy read hides until the builder exposes them: parameters, and a single-view LoRA. */
const LEGACY_HIDDEN_BY_DEFAULT_RANK = 7;

/** Core's default for an input with no stored decision: exposed unless a parameter or a single-view LoRA. */
function isExposedByDefault(node: Node): boolean {
  return legacyInputRank(node) < LEGACY_HIDDEN_BY_DEFAULT_RANK;
}

function legacyInputRank(node: Node): number {
  if (node.type === 'multilora' && (node.data as { viewMode?: string }).viewMode === 'single') {
    return LEGACY_SINGLE_LORA_RANK;
  }
  return LEGACY_INPUT_TYPE_RANK[node.type ?? ''] ?? LEGACY_SINGLE_LORA_RANK;
}

/**
 * The input settings of a tool built before the Tool tab, read off `designAppMetadata` the way
 * core's legacy read does: a stored setting wins; without one, an input takes its place in the
 * legacy type order and is exposed unless it is a parameter or a single-view LoRA. `disabled` is
 * dropped, as the Tool tab has no such state. Without this the conversion would save every input as
 * exposed, showing the ones the builder had hidden.
 */
export function getLegacyToolInputs(
  { nodes, edges }: AppBuilderGraph,
  outputs: ToolConfig['outputs'],
  settings: LegacyInputSettings | null | undefined,
): ToolConfigInput[] {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const { inputNodes, fedSelectorIds } = collectToolInputNodes(
    nodes,
    edges,
    outputs.map(({ nodeId }) => nodeId).filter((id) => nodeIds.has(id)),
  );
  // Core's legacy read skips locked inputs, so they number after the rest and the others keep the
  // positions the live tool shows.
  return inputNodes
    .map((node, index) => ({ node, locked: Boolean(node.data.isLocked), rank: legacyInputRank(node), index }))
    .sort((a, b) => Number(a.locked) - Number(b.locked) || a.rank - b.rank || a.index - b.index)
    .map(({ node, locked }, sequence) => {
      const setting = settings?.[node.id];
      return {
        node,
        order: typeof setting?.order === 'number' ? setting.order : sequence,
        sequence,
        // Conversion is the one place a lock still decides exposure: before the Tool tab, locking was
        // the only way to keep an input out, so the import carries that over as `exposed: false`.
        // A List fed by a static source was not an input before the Tool tab, so it imports hidden, as core reads it.
        exposed:
          !locked &&
          (typeof setting?.exposed === 'boolean'
            ? setting.exposed
            : isExposedByDefault(node) && !fedSelectorIds.has(node.id)),
        setting,
      };
    })
    .sort((a, b) => a.order - b.order || a.sequence - b.sequence)
    .map(({ node, exposed, setting }) => ({
      nodeId: node.id,
      exposed,
      ...(typeof setting?.required === 'boolean' ? { required: setting.required } : {}),
      ...(typeof setting?.description === 'string' ? { description: setting.description } : {}),
    }));
}

function hasToolConfigInputs(config: ToolConfig): config is ToolConfigWithInputs {
  return Array.isArray(config.inputs);
}

/**
 * The stored config, or for a workflow that has none yet, one imported from its legacy output nodes
 * and its old input settings. Nothing is written here: the import is persisted by the first edit in
 * the Tool tab, and until then core falls back to the same legacy nodes and settings. Absent means
 * no `outputs` array, the rule `getToolOutputNodeIds` and core's `parseToolConfig` use, so a builder
 * who removed every output does not get the legacy ones back.
 */
export function resolveToolConfig(
  toolConfig: ToolConfig | null | undefined,
  graph: AppBuilderGraph,
  legacy: LegacyImportContext = {},
): ToolConfigWithInputs {
  if (Array.isArray(toolConfig?.outputs)) {
    // Core treats `inputs` as optional, so a stored config can lack it.
    return hasToolConfigInputs(toolConfig) ? toolConfig : { ...toolConfig, inputs: [] };
  }
  const outputs = getLegacyToolOutputs(graph, legacy.isOutputHandle);
  // Without declared outputs core serves the legacy rule, where every lock hides, and so does the
  // preview; the snapshot applies from the first write that declares them.
  const { lockedAtConversion: _notYetApplied, ...stored } = toolConfig ?? {};
  return {
    ...EMPTY_TOOL_CONFIG,
    ...stored,
    outputs,
    inputs: Array.isArray(toolConfig?.inputs)
      ? toolConfig.inputs
      : getLegacyToolInputs(graph, outputs, legacy.inputSettings),
  };
}

const NO_HIDDEN_LEGACY_OUTPUT_NODES: ReadonlySet<NodeId> = new Set();

/**
 * The legacy `workflow_output` nodes the canvas hides once the Tool tab is on: those wired, directly or
 * through routers, from a node that is now a declared output in their place, by the same config-first rule as {@link resolveToolConfig}.
 * A node is hidden only when every wire into it is replaced.
 * Any other one stays visible, whether it is still the output itself or does nothing, so it can be deleted.
 */
export function getHiddenLegacyOutputNodeIds(
  toolConfig: ToolConfig | null | undefined,
  graph: AppBuilderGraph,
  isOutputHandle: LegacyImportContext['isOutputHandle'],
): ReadonlySet<NodeId> {
  if (!graph.nodes.some((node) => node.type === NodeType.WorkflowOutput)) {
    return NO_HIDDEN_LEGACY_OUTPUT_NODES;
  }
  const outputs = Array.isArray(toolConfig?.outputs) ? toolConfig.outputs : getLegacyToolOutputs(graph, isOutputHandle);
  const declared = new Set(outputs.map((output) => output.nodeId));
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const replaced = new Set<NodeId>();
  const kept = new Set<NodeId>();
  for (const edge of graph.edges) {
    if (nodesById.get(edge.target)?.type !== NodeType.WorkflowOutput) {
      continue;
    }
    const wire = throughRouters(edge, nodesById, graph.edges);
    const producer = nodesById.get(wire.source);
    // Replaced only by a declared node that reads what this wire carried.
    const isReplaced =
      producer !== undefined &&
      declared.has(producer.id) &&
      declaredProducerReadsWire(producer, wire.sourceHandle?.split('-output-')[1]);
    (isReplaced ? replaced : kept).add(edge.target);
  }
  // One wire nothing replaced keeps the node, so hiding it never hides that wire too.
  return new Set([...replaced].filter((nodeId) => !declared.has(nodeId) && !kept.has(nodeId)));
}

/** Adapts the stored config to the drawer's view shape, filling the inputs in from the graph. */
export function toWorkflowApp(workflowId: string, config: ToolConfigWithInputs, graph: AppBuilderGraph): WorkflowApp {
  const outputs: DraftOutputOverride[] = config.outputs.map(({ nodeId, examples, uploads }, order) => ({
    nodeId,
    order,
    ...(examples === undefined || examples.length === 0 ? {} : { examples }),
    ...(uploads === undefined || uploads.length === 0 ? {} : { uploads }),
  }));
  const inputs: DraftInputOverride[] = deriveWorkflowAppInputs({
    currentInputs: config.inputs.map(({ nodeId, exposed, required, description, defaultValue }, order) => ({
      nodeId,
      exposed,
      order,
      required,
      description,
      ...(defaultValue === undefined ? {} : { defaultValue }),
    })),
    outputs,
    nodes: graph.nodes,
    edges: graph.edges,
    lockedAtConversion: config.lockedAtConversion ?? null,
  });

  return { id: workflowId, workflowId, draftOverrides: { inputs, outputs } };
}

/** The view shape back to what the column holds. Order is array position, so there is no field. */
export function toToolConfig(draftOverrides: WorkflowAppDraftOverrides): ToolConfigWithInputs {
  return {
    version: 1,
    outputs: [...draftOverrides.outputs]
      .sort((a, b) => a.order - b.order)
      .map(({ nodeId, examples, uploads }) => ({
        nodeId,
        ...(examples === undefined || examples.length === 0 ? {} : { examples }),
        ...(uploads === undefined || uploads.length === 0 ? {} : { uploads }),
      })),
    inputs: [...draftOverrides.inputs]
      .sort((a, b) => a.order - b.order)
      .map(({ nodeId, exposed, description, required, defaultValue }) => ({
        nodeId,
        exposed,
        // `required` is tri-state: omit it to keep asking the graph. Only an explicit popover
        // override is written, so a derived false cannot freeze after the node is rewired.
        ...(required === undefined ? {} : { required }),
        ...(description === undefined ? {} : { description }),
        ...(defaultValue === undefined ? {} : { defaultValue }),
      })),
  };
}
